import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from './api';
import { useAuth } from './auth';
import { useI18n } from './i18n';
import QuestionPlayer, {
  QuestionPlayerQuestion,
  QuestionScope,
} from './QuestionPlayer';
import { getPublicDemoQuestion, PUBLIC_DEMO_QUESTION_LIMIT } from './publicDemoQuestions';
import './QuestionDemoPage.css';

export default function QuestionDemoPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { language, t } = useI18n();
  const [scope, setScope] = useState<QuestionScope>('PODSTAWOWY');
  const [publicQuestionIndex, setPublicQuestionIndex] = useState(0);
  const [publicPreviewComplete, setPublicPreviewComplete] = useState(false);
  const [question, setQuestion] = useState<QuestionPlayerQuestion | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const copy = t.questionDemo;
  const publicQuestion = getPublicDemoQuestion(publicQuestionIndex, language);
  const activeQuestion = isAuthenticated ? question : publicPreviewComplete ? null : publicQuestion;

  async function loadQuestion() {
    if (!isAuthenticated) {
      return;
    }

    if (isAuthenticated && !user?.preferred_exam_category) {
      return;
    }

    setIsFetching(true);
    setFetchError('');

    try {
      const params = { language, scope };
      const response = await api.get<QuestionPlayerQuestion>('/study/demo-question/', { params });
      setQuestion(response.data);
    } catch (error: any) {
      const apiError = error?.response?.data?.error;
      setQuestion(null);
      setFetchError(typeof apiError === 'string' ? apiError : copy.fetchErrorFallback);
    } finally {
      setIsFetching(false);
    }
  }

  function handleNextQuestion() {
    if (isAuthenticated) {
      void loadQuestion();
      return;
    }

    if (publicQuestionIndex >= PUBLIC_DEMO_QUESTION_LIMIT - 1) {
      setPublicPreviewComplete(true);
      return;
    }

    setPublicQuestionIndex((current) => current + 1);
  }

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    setPublicQuestionIndex(0);
    setPublicPreviewComplete(false);
  }, [isAuthenticated, language]);

  useEffect(() => {
    if (!isAuthenticated || !user?.preferred_exam_category) {
      if (isAuthenticated) {
        setQuestion(null);
      }
      return;
    }

    void loadQuestion();
  }, [isAuthenticated, language, scope, user?.preferred_exam_category]);

  return (
    <div className="question-demo-page">
      <div className="question-demo-page__shell">
        <section className="question-demo-page__panel question-demo-page__intro">
          <h1 className="question-demo-page__title">{copy.pageTitle}</h1>
          <p className="question-demo-page__description">{copy.pageDescription}</p>
        </section>

        {isLoading ? (
          <section className="question-demo-page__panel question-demo-page__message">{copy.loading}</section>
        ) : (
          <>
            {isAuthenticated ? (
              <section className="question-demo-page__panel question-demo-page__controls">
                <label className="question-demo-page__field">
                  <span className="question-demo-page__label">{copy.scopeLabel}</span>
                  <select
                    className="question-demo-page__select"
                    value={scope}
                    onChange={(event) => setScope(event.target.value as QuestionScope)}
                  >
                    <option value="PODSTAWOWY">{copy.scopeBasic}</option>
                    <option value="SPECJALISTYCZNY">{copy.scopeSpecialist}</option>
                  </select>
                </label>

                <button
                  type="button"
                  className="question-demo-page__button"
                  onClick={() => void loadQuestion()}
                  disabled={isFetching || !user?.preferred_exam_category}
                >
                  {isFetching ? copy.loadingQuestion : copy.loadQuestion}
                </button>
              </section>
            ) : (
              <section className="question-demo-page__panel question-demo-page__message">
                {copy.publicPreviewNotice}
              </section>
            )}

            {fetchError ? <section className="question-demo-page__panel question-demo-page__error">{fetchError}</section> : null}

            {publicPreviewComplete && !isAuthenticated ? (
              <section className="question-demo-page__panel question-demo-page__login-gate">
                <h2>{copy.signupModalTitle}</h2>
                <p>{copy.signupModalText}</p>
                <div className="question-demo-page__login-actions">
                  <Link to="/login" className="question-demo-page__button question-demo-page__button--link">
                    {t.home.login}
                  </Link>
                </div>
              </section>
            ) : null}

            {activeQuestion ? (
              <div className="content-layout">
                <QuestionPlayer
                  question={activeQuestion}
                  language={language}
                  questionOrder={isAuthenticated ? 1 : publicQuestionIndex + 1}
                  totalQuestions={isAuthenticated ? 1 : PUBLIC_DEMO_QUESTION_LIMIT}
                  answeredCount={0}
                  remainingCount={isAuthenticated ? 0 : PUBLIC_DEMO_QUESTION_LIMIT - publicQuestionIndex - 1}
                  examTimeLeft={0}
                  examTimeLabel={t.exam.globalTime}
                  answeredLabel={t.exam.answeredCount}
                  remainingLabel={t.exam.remainingCount}
                  nextButtonLabel={
                    !isAuthenticated && publicQuestionIndex >= PUBLIC_DEMO_QUESTION_LIMIT - 1
                      ? t.home.login
                      : copy.loadQuestion
                  }
                  nextButtonDisabled={isFetching}
                  showSidebar={false}
                  onNextQuestion={handleNextQuestion}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
